/**
 * Mock API for Subscriptions
 * This provides sample data for development until real APIM endpoints are available
 */

import { SubscriptionContract } from '../contracts/subscription';
import { ProductContract } from '../contracts/product';
import apiClient from './apiClient';

// Mock products data
const mockProducts: ProductContract[] = [
    {
        id: 'product-1',
        name: 'starter-api',
        displayName: 'Starter API',
        description: 'Basic API access for getting started',
        terms: 'Standard terms and conditions apply',
        subscriptionRequired: true,
        approvalRequired: false,
        state: 'published',
    },
    {
        id: 'product-2',
        name: 'premium-api',
        displayName: 'Premium API',
        description: 'Advanced API access with higher rate limits',
        terms: 'Premium terms and conditions apply',
        subscriptionRequired: true,
        approvalRequired: true,
        state: 'published',
    },
];

// Mock data generator
const generateMockSubscriptions = (count: number = 1): SubscriptionContract[] => {
    const states: ('active' | 'suspended' | 'cancelled' | 'expired')[] = ['active', 'active', 'active', 'suspended'];
    const products = mockProducts;
    
    return Array.from({ length: count }, (_, i) => {
        const createdDate = new Date();
        createdDate.setDate(createdDate.getDate() - (i * 10));
        
        const product = products[i % products.length];
        return {
            id: `subscription-${i + 1}`,
            name: `sub-${Date.now()}-${i}`,
            displayName: `My ${product.displayName} Subscription`,
            state: states[i % states.length],
            createdDate: createdDate.toISOString(),
            startDate: createdDate.toISOString(),
            expirationDate: new Date(createdDate.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: undefined,
            primaryKey: `pk_${generateRandomKey()}`,
            secondaryKey: `sk_${generateRandomKey()}`,
            stateComment: undefined,
            allowTracing: true,
            ownerId: '/users/current',
            scope: `/products/${product.id}`,
            productId: product.id,
            productName: product.displayName,
            productDescription: product.description,
        };
    });
};

const generateRandomKey = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
        key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
};

// Mock API delay
const delay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// In-memory storage for subscriptions (simulates backend)
let subscriptions = generateMockSubscriptions(1);

/**
 * Mock API functions
 */

export const mockSubscriptionsAPI = {
    /**
     * Get all subscriptions for the current user
     */
    async getUserSubscriptions(): Promise<SubscriptionContract[]> {
        await delay(300);
        return [...subscriptions];
    },

    /**
     * Get a specific subscription by ID
     */
    async getSubscription(subscriptionId: string): Promise<SubscriptionContract> {
        await delay(200);
        const subscription = subscriptions.find(s => s.id === subscriptionId);
        if (!subscription) {
            throw new Error(`Subscription ${subscriptionId} not found`);
        }
        return { ...subscription };
    },

    /**
     * Get all available products
     */
    async getProducts(): Promise<ProductContract[]> {
        await delay(200);
        return [...mockProducts];
    },

    /**
     * Create a new subscription
     */
    async createSubscription(
        productId: string,
        displayName: string,
        allowTracing: boolean = false
    ): Promise<SubscriptionContract> {
        await delay(400);
        
        const product = mockProducts.find(p => p.id === productId);
        if (!product) {
            throw new Error(`Product ${productId} not found`);
        }

        const newSubscription: SubscriptionContract = {
            id: `subscription-${Date.now()}`,
            name: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            displayName: displayName,
            state: product.approvalRequired ? 'submitted' : 'active',
            createdDate: new Date().toISOString(),
            startDate: new Date().toISOString(),
            expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: undefined,
            primaryKey: `pk_${generateRandomKey()}`,
            secondaryKey: `sk_${generateRandomKey()}`,
            stateComment: product.approvalRequired ? 'Pending approval' : undefined,
            allowTracing: allowTracing,
            ownerId: '/users/current',
            scope: `/products/${productId}`,
            productId: productId,
            productName: product.displayName,
            productDescription: product.description,
        };

        subscriptions = [...subscriptions, newSubscription];
        return { ...newSubscription };
    },

    /**
     * Cancel a subscription
     */
    async cancelSubscription(subscriptionId: string): Promise<void> {
        await delay(300);
        
        const index = subscriptions.findIndex(s => s.id === subscriptionId);
        if (index === -1) {
            throw new Error(`Subscription ${subscriptionId} not found`);
        }

        // Remove the subscription from the list
        subscriptions = subscriptions.filter(s => s.id !== subscriptionId);
    },

    /**
     * Rename a subscription
     */
    async renameSubscription(
        subscriptionId: string,
        newDisplayName: string
    ): Promise<SubscriptionContract> {
        await delay(300);
        
        const subscription = subscriptions.find(s => s.id === subscriptionId);
        if (!subscription) {
            throw new Error(`Subscription ${subscriptionId} not found`);
        }

        subscription.displayName = newDisplayName;
        return { ...subscription };
    },

    /**
     * Regenerate a subscription key
     */
    async regenerateKey(
        subscriptionId: string,
        keyType: 'primary' | 'secondary'
    ): Promise<SubscriptionContract> {
        await delay(400);
        
        const subscription = subscriptions.find(s => s.id === subscriptionId);
        if (!subscription) {
            throw new Error(`Subscription ${subscriptionId} not found`);
        }

        if (keyType === 'primary') {
            subscription.primaryKey = `pk_${generateRandomKey()}`;
        } else {
            subscription.secondaryKey = `sk_${generateRandomKey()}`;
        }

        return { ...subscription };
    },
};

/**
 * Real API functions for Subscriptions
 */

export const subscriptionsAPI = {
    /**
     * Get all subscriptions for the current user
     * GET /users/self/subscriptions
     * Response shape: { data: SubscriptionContract[] }
     */
    async getUserSubscriptions(): Promise<SubscriptionContract[]> {
        try {
            const response = await apiClient.get(`/appmgr/users/self/subscriptions`) as any;

            // Response is already an array
            if (Array.isArray(response)) {
                return response;
            }
            // API wraps subscriptions in { data: [...] }
            if (response?.data && Array.isArray(response.data)) {
                return response.data;
            }
            // Fallback: check other possible wrapper keys
            if (response?.value && Array.isArray(response.value)) {
                return response.value;
            }
            if (response?.subscriptions && Array.isArray(response.subscriptions)) {
                return response.subscriptions;
            }

            return [];
        } catch (error: any) {
            // Return empty array if no subscriptions found
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    },

    /**
     * Rename a subscription
     * PATCH /users/self/subscriptions/{ownerId}
     * Payload: { "displayName": "MySub4-1" }
     */
    async renameSubscription(ownerId: string, newDisplayName: string): Promise<SubscriptionContract> {
        const response = await apiClient.patch(
            `/appmgr/users/self/subscriptions/${ownerId}`,
            { displayName: newDisplayName }
        ) as any;
        return response;
    },

    /**
     * Cancel a subscription
     * POST /users/self/subscriptions/{subscriptionId}/cancel
     */
    async cancelSubscription(subscriptionId: string): Promise<void> {
        await apiClient.post(`/appmgr/users/self/subscriptions/${subscriptionId}/cancel`, {});
    },

    /**
     * Get a specific subscription by ID
     * GET /users/self/subscriptions/{subscriptionId}
     */
    async getSubscription(subscriptionId: string): Promise<SubscriptionContract> {
        const response = await apiClient.get(
            `/appmgr/users/self/subscriptions/${subscriptionId}`
        ) as any;
        return response;
    },

    /**
     * Create a new subscription
     * POST /users/self/subscriptions
     * Payload: { "displayName": "MySub4" }
     */
    async createSubscription(displayName: string): Promise<SubscriptionContract> {
        const response = await apiClient.post(
            `/appmgr/users/self/subscriptions`,
            { displayName }
        ) as any;
        return response;
    },

    /**
     * Regenerate subscription key
     * POST /users/self/subscriptions/{subscriptionId}/secret-keys
     */
    async regenerateKey(subscriptionId: string, keyType: 'primary' | 'secondary'): Promise<void> {
        await apiClient.post(
            `/appmgr/users/self/subscriptions/${subscriptionId}/secret-keys`,
            { keyType: keyType.toUpperCase() }
        );
    },

    /**
     * Get all products
     * TODO: Replace with real API endpoint when available
     */
    async getProducts(): Promise<ProductContract[]> {
        // For now, using mock - will be replaced with real API
        return mockSubscriptionsAPI.getProducts();
    },
};

